<template>
	<article>
		<h2>Overzicht</h2>

		<div class="widget-overview">
			<DashboardWidget
				:cta="inventoryCta"
				:loading="data.inventarisation.loading"
				:value="`${data.inventarisation.data.percentage} %`"
				data-testid="widget-inventarisation"
				label="Status"
				title="Inventarisatie"
			/>

			<DashboardWidget
				:cta="risksCta"
				:loading="data.risks.loading"
				:value="data.risks.data.total"
				data-testid="widget-risks"
				label="Alle risico's"
				title="Risico's"
			>
				<DashboardWidgetButton
					text="Risico's zonder actie"
					:to="risksWithoutActionsOverview"
					:color-for-number="data.risks.data.noActions > 0 ? 'danger' : 'neutral'"
					:number="data.risks.loading ? '-' : data.risks.data.noActions"
				/>
			</DashboardWidget>

			<DashboardWidget
				:cta="actionsCta"
				:loading="data.actions.loading"
				:value="data.actions.data.total"
				data-testid="widget-actions"
				label="Alle acties"
				title="Acties"
			>
				<!-- Geplande acties is the default tab for action overview -->
				<DashboardWidgetButton
					text="Geplande acties"
					:to="actionsCta.to"
					:color-for-number="data.actions.data.openActions.length > 0 ? 'warning' : 'neutral'"
					:number="data.actions.loading ? '-' : data.actions.data.openActions.length"
				/>
			</DashboardWidget>
		</div>
	</article>
</template>

<script lang="ts" setup>
	import {computed} from 'vue';
	import type {PropType} from 'vue';
	import type {RouteLocationRaw} from 'vue-router';
	import type {ButtonClass} from '~/lib/types/ButtonTypes';
	import {pageNames} from '~/lib/pageHelper';
	import type {DashboardOverviewData} from '~/lib/types/dashboardTypes';
	import DashboardWidgetButton from '~/components/dashboard/DashboardWidgetButton.vue';

	const props = defineProps({
		data: {
			type: Object as PropType<DashboardOverviewData>,
			required: true
		},
		companyId: {
			type: Number,
			required: true
		},
		rieId: {
			type: String,
			required: true
		},
		readOnly: {
			type: Boolean,
			required: false
		}
	});

	type cta = {
		text: string,
		to: RouteLocationRaw,
		btnClass: ButtonClass
	}

	const params = {
		companyId: props.companyId,
		rieId: props.rieId
	};

	const inventoryCta = computed<cta>(() => ({
		to: {
			name: pageNames.inventarisation,
			params
		},
		text: props.data.inventarisation.data.percentage < 100 && !props.readOnly ? 'Inventariseer' : 'Overzicht',
		btnClass: props.data.inventarisation.data.percentage < 100 && !props.readOnly ? 'primary' : 'secondary'
	}));

	const risksCta = computed<cta>(() => ({
		to: {
			name: pageNames.riskOverview,
			params
		},
		text: 'Overzicht',
		btnClass: 'secondary'
	}));

	const risksWithoutActionsOverview = computed<RouteLocationRaw>(() => {
		return {
			name: pageNames.riskOverview,
			params,
			query: {
				'filter[filterAction]': 'NO_ACTIONS'
			}
		};
	});

	const actionsCta = computed<cta>(() => ({
		to: {
			name: pageNames.actionOverview,
			params
		},
		text: 'Overzicht',
		btnClass: 'secondary'
	}));
</script>

<style scoped>
	h2 {
		font-size: var(--font-size-p-4);
		line-height: var(--line-height-p-4);
		font-weight: bold;
		margin: 0 0 10px;
	}

	.widget-overview {
		display: grid;
		gap: 10px;
		grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
		align-items: stretch;
	}
</style>
